import type { CharacterId, ContentLevel } from '@/types'

// Milestone thresholds within each level
export const MILESTONE_THRESHOLDS = [0.25, 0.5, 0.75, 1.0] as const
export type MilestonePercent = typeof MILESTONE_THRESHOLDS[number]

// Milestone types
export type MilestoneType = 'quarter' | 'half' | 'three_quarter' | 'level_up'

export interface Milestone {
  percent: MilestonePercent
  type: MilestoneType
  gemReward: number
  affectionBonus: number
}

export const MILESTONES: Record<MilestonePercent, Milestone> = {
  0.25: {
    percent: 0.25,
    type: 'quarter',
    gemReward: 5,
    affectionBonus: 5,
  },
  0.5: {
    percent: 0.5,
    type: 'half',
    gemReward: 10,
    affectionBonus: 10,
  },
  0.75: {
    percent: 0.75,
    type: 'three_quarter',
    gemReward: 15,
    affectionBonus: 15,
  },
  1.0: {
    percent: 1.0,
    type: 'level_up',
    gemReward: 50,
    affectionBonus: 25,
  },
}

// Character reactions at each milestone - gets spicier as you progress!
export const MILESTONE_REACTIONS: Record<CharacterId, Record<MilestoneType, {
  dialogue: string[]
  pose: string // placeholder for pose asset reference
  animation: 'wink' | 'blush' | 'excited' | 'celebrate'
}>> = {
  hana: {
    quarter: {
      dialogue: [
        "Ooh~ You're making progress!",
        "Keep going, I believe in you~!",
        "That's a good start! *wink*"
      ],
      pose: 'wink',
      animation: 'wink',
    },
    half: {
      dialogue: [
        "Halfway there! You're amazing~!",
        "Mmm~ I'm getting excited watching you work!",
        "Don't stop now... I'm cheering for you! *bounce*"
      ],
      pose: 'excited',
      animation: 'excited',
    },
    three_quarter: {
      dialogue: [
        "Almost there~! I can barely contain myself!",
        "So close! My heart is pounding~!",
        "You're incredible... *blush* Keep going!"
      ],
      pose: 'blush',
      animation: 'blush',
    },
    level_up: {
      dialogue: [
        "KYAAA~! You did it! You leveled up!",
        "Amazing! I'm so proud of you~! *jumps excitedly*",
        "You're the best! Let me celebrate with you~!"
      ],
      pose: 'celebrate',
      animation: 'celebrate',
    },
  },
  yuki: {
    quarter: {
      dialogue: [
        "...Not bad. Continue.",
        "Hmph. You're actually trying.",
        "A quarter done? ...Acceptable."
      ],
      pose: 'wink',
      animation: 'wink',
    },
    half: {
      dialogue: [
        "Halfway... I'm mildly impressed.",
        "You're... not as lazy as I thought. *looks away*",
        "Keep this pace. Don't disappoint me."
      ],
      pose: 'excited',
      animation: 'blush',
    },
    three_quarter: {
      dialogue: [
        "Almost there... *clears throat* Good work.",
        "I... didn't think you'd get this far. *blush*",
        "Don't get cocky, but... you're doing well."
      ],
      pose: 'blush',
      animation: 'blush',
    },
    level_up: {
      dialogue: [
        "You... actually did it. *small smile*",
        "Level up? I suppose you've earned my respect.",
        "...Fine. I'll admit it. You impressed me. *looks away blushing*"
      ],
      pose: 'celebrate',
      animation: 'celebrate',
    },
  },
  momo: {
    quarter: {
      dialogue: [
        "Y-you're doing great...! *fidgets*",
        "K-keep going... I'm watching... *blush*",
        "That's wonderful...! *hides behind hands*"
      ],
      pose: 'wink',
      animation: 'blush',
    },
    half: {
      dialogue: [
        "H-halfway! You're so cool...! *squirms*",
        "I-I'm so happy for you...! *heart racing*",
        "A-amazing...! *peeks through fingers*"
      ],
      pose: 'excited',
      animation: 'blush',
    },
    three_quarter: {
      dialogue: [
        "S-so close...! My heart can't take it...!",
        "Y-you're incredible...! *fans face*",
        "I-I'm getting nervous for you...! *trembling*"
      ],
      pose: 'blush',
      animation: 'blush',
    },
    level_up: {
      dialogue: [
        "Y-YOU DID IT! *tackle hug*",
        "I-I'm so proud...! *tears of joy*",
        "A-amazing...! You're my hero...! *clings*"
      ],
      pose: 'celebrate',
      animation: 'celebrate',
    },
  },
  sumire: {
    quarter: {
      dialogue: [
        "Ara~ A nice start, dear~",
        "You're doing well... let me reward you~",
        "Keep going, sweetie~"
      ],
      pose: 'wink',
      animation: 'wink',
    },
    half: {
      dialogue: [
        "Halfway there~ How exciting~",
        "Such dedication... I'm impressed~",
        "You're making me proud, dear~"
      ],
      pose: 'excited',
      animation: 'excited',
    },
    three_quarter: {
      dialogue: [
        "Almost there~ Don't give up now~",
        "You're doing wonderfully, darling~",
        "So close... I can feel your determination~"
      ],
      pose: 'blush',
      animation: 'blush',
    },
    level_up: {
      dialogue: [
        "Congratulations, dear~ You did it~!",
        "I knew you could do it~ Let me celebrate with you~",
        "Such a good job~ You deserve a special reward~"
      ],
      pose: 'celebrate',
      animation: 'celebrate',
    },
  },
}

// Animation intensity increases with content level and milestone
export const ANIMATION_INTENSITY: Record<ContentLevel, Record<MilestoneType, {
  bounceScale: number
  jiggleAmount: number
  duration: number
}>> = {
  1: {
    quarter: { bounceScale: 1.02, jiggleAmount: 0, duration: 0.5 },
    half: { bounceScale: 1.03, jiggleAmount: 0.5, duration: 0.6 },
    three_quarter: { bounceScale: 1.04, jiggleAmount: 1, duration: 0.7 },
    level_up: { bounceScale: 1.05, jiggleAmount: 1.5, duration: 1 },
  },
  2: {
    quarter: { bounceScale: 1.03, jiggleAmount: 1, duration: 0.5 },
    half: { bounceScale: 1.05, jiggleAmount: 2, duration: 0.6 },
    three_quarter: { bounceScale: 1.07, jiggleAmount: 3, duration: 0.8 },
    level_up: { bounceScale: 1.1, jiggleAmount: 4, duration: 1.2 },
  },
  3: {
    quarter: { bounceScale: 1.05, jiggleAmount: 2, duration: 0.5 },
    half: { bounceScale: 1.08, jiggleAmount: 4, duration: 0.7 },
    three_quarter: { bounceScale: 1.1, jiggleAmount: 5, duration: 0.9 },
    level_up: { bounceScale: 1.15, jiggleAmount: 7, duration: 1.5 },
  },
  4: {
    quarter: { bounceScale: 1.08, jiggleAmount: 4, duration: 0.6 },
    half: { bounceScale: 1.12, jiggleAmount: 6, duration: 0.8 },
    three_quarter: { bounceScale: 1.15, jiggleAmount: 8, duration: 1 },
    level_up: { bounceScale: 1.2, jiggleAmount: 10, duration: 2 },
  },
}

// Level up rewards - unlocks get spicier at higher levels
export interface LevelReward {
  gems: number
  outfitUnlock?: string
  poseUnlock?: string
  specialAnimation?: boolean
}

export const LEVEL_REWARDS: Record<number, LevelReward> = {
  5: { gems: 100, outfitUnlock: 'casual_revealing' },
  10: { gems: 200, poseUnlock: 'playful', specialAnimation: true },
  15: { gems: 300, outfitUnlock: 'swimsuit' },
  20: { gems: 400, poseUnlock: 'seductive' },
  25: { gems: 500, outfitUnlock: 'lingerie', specialAnimation: true },
  30: { gems: 600, poseUnlock: 'intimate' },
  40: { gems: 800, outfitUnlock: 'bunny_suit', specialAnimation: true },
  50: { gems: 1000, outfitUnlock: 'special_edition', poseUnlock: 'ultimate', specialAnimation: true },
}

// Get the current milestone based on XP progress
export function getCurrentMilestone(currentXp: number, xpForLevel: number): MilestonePercent | null {
  const progress = currentXp / xpForLevel

  for (const threshold of MILESTONE_THRESHOLDS) {
    if (progress < threshold) {
      return null
    }
    if (progress >= threshold && progress < (MILESTONE_THRESHOLDS[MILESTONE_THRESHOLDS.indexOf(threshold) + 1] || 2)) {
      return threshold
    }
  }
  return null
}

// Check if a milestone was just crossed
export function checkMilestoneCrossed(
  previousXp: number,
  newXp: number,
  xpForLevel: number
): MilestonePercent | null {
  const previousProgress = previousXp / xpForLevel
  const newProgress = newXp / xpForLevel

  for (const threshold of MILESTONE_THRESHOLDS) {
    if (previousProgress < threshold && newProgress >= threshold) {
      return threshold
    }
  }
  return null
}
